import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { t } from "@/lib/i18n";
import { toast } from "sonner";
import { Sparkles, Wallet, Loader2 } from "lucide-react";

const chf = (n) =>
  new Intl.NumberFormat("de-CH", { style: "currency", currency: "CHF" }).format(n || 0);

/** Panel shown INSIDE the /dashboard/module/product or /pricing pages.
 *  `mode` = "product" | "pricing". */
export function IntelligencePanel({ mode }) {
  const { lang } = useLang();
  const [products, setProducts] = useState([]);
  const [enrichments, setEnrichments] = useState({});
  const [pricings, setPricings] = useState({});
  const [busy, setBusy] = useState({});
  const [batchBusy, setBatchBusy] = useState(false);
  const isProduct = mode === "product";

  const load = async () => {
    const [p, e, pr] = await Promise.all([
      api.get("/shopify/products?limit=200"),
      api.get("/intelligence/product"),
      api.get("/intelligence/pricing"),
    ]);
    setProducts(p.data.products || []);
    setEnrichments(Object.fromEntries((e.data.enrichments || []).map((d) => [d.shopify_id, d])));
    setPricings(Object.fromEntries((pr.data.pricing || []).map((d) => [d.shopify_id, d])));
  };

  useEffect(() => { load(); }, []);

  const run = async (sid) => {
    setBusy((b) => ({ ...b, [sid]: true }));
    try {
      const url = isProduct ? `/intelligence/product/${sid}/enrich` : `/intelligence/pricing/${sid}`;
      const { data } = await api.post(url);
      if (isProduct) setEnrichments((m) => ({ ...m, [sid]: data }));
      else setPricings((m) => ({ ...m, [sid]: data }));
      toast.success(isProduct ? t(lang, "intel_enriched") : t(lang, "intel_priced"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy((b) => ({ ...b, [sid]: false }));
    }
  };

  const runAll = async () => {
    setBatchBusy(true);
    for (const p of products) {
      const already = isProduct ? enrichments[p.shopify_id] : pricings[p.shopify_id];
      if (already) continue;
      await run(p.shopify_id);
    }
    setBatchBusy(false);
  };

  const doneCount = products.filter((p) => (isProduct ? enrichments : pricings)[p.shopify_id]).length;

  return (
    <div className="hq-card overflow-hidden" data-testid={`${mode}-intel-panel`}>
      <div className="p-5 border-b border-white/10 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          {isProduct ? <Sparkles size={14} className="text-[color:var(--hq-cyan)]" /> : <Wallet size={14} className="text-[color:var(--hq-cyan)]" />}
          <div className="font-display text-lg">{isProduct ? t(lang, "intel_product_title") : t(lang, "intel_pricing_title")}</div>
          <span className="font-mono text-xs text-white/40">{doneCount} / {products.length}</span>
        </div>
        <button
          data-testid={`${mode}-run-all-btn`}
          onClick={runAll}
          disabled={batchBusy || products.length === 0}
          className="hq-btn hq-btn-primary disabled:opacity-50"
        >
          {batchBusy && <Loader2 size={12} className="animate-spin" />}
          {batchBusy ? t(lang, "intel_running_all") : t(lang, "intel_run_all")}
        </button>
      </div>

      {products.length === 0 ? (
        <div className="text-center py-12 font-mono text-xs text-white/40">
          {t(lang, "intel_no_products")}
        </div>
      ) : (
        <div className="p-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((p) => (
            <IntelCard
              key={p.shopify_id}
              p={p}
              lang={lang}
              mode={mode}
              isProduct={isProduct}
              enrichment={enrichments[p.shopify_id]}
              pricing={pricings[p.shopify_id]}
              busy={busy[p.shopify_id]}
              batchBusy={batchBusy}
              onRun={() => run(p.shopify_id)}
              onPublish={async () => {
                setBusy((b) => ({ ...b, [p.shopify_id]: true }));
                try {
                  const { data } = await api.post(`/intelligence/product/${p.shopify_id}/publish`);
                  const nErr = data.user_errors?.length || 0;
                  if (nErr === 0) toast.success(`Shopify: ${data.entries_registered} translations in ${data.published_locales.join(', ')}`);
                  else toast.warning(`Published with ${nErr} errors`);
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Publish failed");
                } finally {
                  setBusy((b) => ({ ...b, [p.shopify_id]: false }));
                }
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}


function IntelCard({ p, lang, mode, isProduct, enrichment, pricing, busy, batchBusy, onRun, onPublish }) {
  const done = isProduct ? !!enrichment : !!pricing;
  const chf = (n) => new Intl.NumberFormat("de-CH", { style: "currency", currency: "CHF" }).format(n || 0);

  return (
    <div
      data-testid={`${mode}-row-${p.shopify_id}`}
      className="border border-white/10 rounded-lg overflow-hidden bg-white/[0.02] hover:border-[color:var(--hq-cyan)]/40 transition-colors duration-300 flex flex-col"
    >
      <div className="aspect-[16/9] bg-[#0a0a0a] relative overflow-hidden">
        {p.image ? (
          <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/15 font-mono text-[10px]">
            no image
          </div>
        )}
        {done && (
          <div className="absolute top-2 right-2 text-[9px] font-mono uppercase tracking-widest border border-[color:var(--hq-success)]/40 text-[color:var(--hq-success)] bg-[color:var(--hq-success)]/5 px-2 py-0.5 rounded-full">
            ✓ done
          </div>
        )}
      </div>

      <div className="p-4 flex-1">
        <div className="text-sm text-white font-medium line-clamp-2 min-h-[2.5rem]">{p.title}</div>

        {isProduct ? (
          <div className="mt-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-1.5">
              {t(lang, "intel_col_langs")}
            </div>
            <div className="flex gap-1">
              {["pt", "en", "de", "fr", "it"].map((lg) => (
                <span
                  key={lg}
                  className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border ${
                    enrichment?.title?.[lg]
                      ? "text-[color:var(--hq-success)] border-[color:var(--hq-success)]/40 bg-[color:var(--hq-success)]/5"
                      : "text-white/25 border-white/10"
                  }`}
                >
                  {lg}
                </span>
              ))}
            </div>
            {enrichment?.tags && enrichment.tags.length > 0 && (
              <div className="mt-2 text-[10px] text-white/50 line-clamp-2">
                {enrichment.tags.slice(0, 5).join(" · ")}
              </div>
            )}
          </div>
        ) : (
          <div className="mt-3 grid grid-cols-2 gap-2">
            <div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-white/40">
                {t(lang, "intel_col_current")}
              </div>
              <div className="font-display text-lg text-white/80">
                {pricing ? chf(pricing.current_price_chf) : "—"}
              </div>
            </div>
            <div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-white/40">
                {t(lang, "intel_col_recommended")}
              </div>
              <div className="font-display text-lg text-[color:var(--hq-cyan)]">
                {pricing ? chf(pricing.recommended_price_chf) : "—"}
              </div>
            </div>
            {pricing && (
              <div className="col-span-2 flex items-center gap-2 text-[10px] font-mono">
                <span
                  className={
                    pricing.delta_pct >= 0
                      ? "text-[color:var(--hq-success)]"
                      : "text-[color:var(--hq-warning)]"
                  }
                >
                  {pricing.delta_pct >= 0 ? "+" : ""}
                  {pricing.delta_pct}%
                </span>
                <span className="text-white/30">·</span>
                <span className="text-white/50">
                  confidence {Math.round(pricing.confidence * 100)}%
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="p-3 border-t border-white/10 flex items-center justify-end gap-2 bg-black/20">
        <button
          data-testid={`${mode}-run-${p.shopify_id}`}
          onClick={onRun}
          disabled={busy || batchBusy}
          className="hq-btn hq-btn-ghost text-xs disabled:opacity-50"
        >
          {busy && <Loader2 size={10} className="animate-spin" />}
          {done ? t(lang, "intel_redo") : t(lang, "intel_run")}
        </button>
        {isProduct && done && (
          <button
            data-testid={`product-publish-${p.shopify_id}`}
            onClick={onPublish}
            disabled={busy || batchBusy}
            className="hq-btn hq-btn-cyan text-xs disabled:opacity-50"
          >
            {t(lang, "intel_publish")}
          </button>
        )}
      </div>
    </div>
  );
}
